import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ArrowLeft as BackArrow,
  Save,
  Sparkles,
  Check,
  BookOpen,
  GripVertical,
} from 'lucide-react';
import { Button, Tabs, type TabItem } from '../ui';
import { StepIndicator, type StepIndicatorStep } from './StepIndicator.js';
import { useResizableWidth } from '../../hooks/useResizableWidth.js';

const SIDEBAR_TABS: readonly TabItem[] = [
  { id: 'help', label: 'Help', icon: BookOpen },
  { id: 'assistant', label: 'AI assistant', icon: Sparkles },
];

export interface EntityWizardLayoutProps {
  /** User-facing label — "Template", "Tool", "Employee". */
  entityLabel: string;
  /** Back route (list page). */
  backTo: string;
  /** Ordered step definitions. */
  steps: ReadonlyArray<StepIndicatorStep>;
  /** Zero-based current step. Owned by caller (typically via useWizardState). */
  currentStep: number;
  /** Zero-based furthest-reached step. Drives StepIndicator click-back gating. */
  furthestReached: number;
  onRequestStep: (index: number) => void | Promise<void>;
  onPrevious: () => void;
  onNext: () => void | Promise<void>;
  /** Save-draft action. Disabled when minimal required fields invalid. */
  onSaveDraft: () => void | Promise<void>;
  /** Activate action — only rendered on the final step; disabled when
   *  canActivate() fails. */
  onActivate: () => void | Promise<void>;
  /** Form markup for the current step only. */
  formSlot: ReactNode;
  /** Field-help panel for the current step (built from {entity}-fields.ts). */
  helpSlot: ReactNode;
  /** Optional entity-specific embedded assistant. When omitted, the sidebar
   *  shows only the Field Help tab — pages that use a floating <AgentAssistant>
   *  (the shared primitive) render it as a sibling of this layout instead. */
  assistantSlot?: ReactNode;
  /** Inline <NavigateAwayWarning> — pages pass the guarded component here. */
  navigateAwayWarning: ReactNode;
  /** Header chip shown when wizard is hydrated from a marketplace listing. Hidden otherwise. */
  marketplaceBanner?: ReactNode;
  /** State flags. */
  isSaving: boolean;
  canAdvance: boolean;
  canActivate: boolean;
  canSaveDraft: boolean;
  /** Storage key suffix for sidebar width persistence (unique per entity). */
  sidebarStorageKey: string;
  /** Which sidebar tab opens first. Fresh wizard → 'assistant' (§4.1);
   *  resumed draft or marketplace-hydrated → 'help'. */
  defaultSidebarTab?: 'help' | 'assistant';
}

/**
 * Full-viewport (width + height) wizard shell per ENTITY_CREATION_WIZARD_SPEC.md
 * §8 + §10.1. TypeScript-enforced slots ensure every new-entity wizard has all
 * the required pieces — step indicator, form, help panel, assistant, guard
 * warning — by construction.
 *
 * h-screen flex-col outer shell; form region scrolls independently of the
 * sidebar. Sidebar resizable from the left edge (280 .. 640 px), persisted per
 * entity via useResizableWidth.
 */
export function EntityWizardLayout({
  entityLabel,
  backTo,
  steps,
  currentStep,
  furthestReached,
  onRequestStep,
  onPrevious,
  onNext,
  onSaveDraft,
  onActivate,
  formSlot,
  helpSlot,
  assistantSlot,
  navigateAwayWarning,
  marketplaceBanner,
  isSaving,
  canAdvance,
  canActivate,
  canSaveDraft,
  sidebarStorageKey,
  defaultSidebarTab = 'assistant',
}: EntityWizardLayoutProps) {
  const isLastStep = currentStep === steps.length - 1;
  const [tab, setTab] = useState<'help' | 'assistant'>(defaultSidebarTab);
  const { width: sidebarWidth, onResizeStart } = useResizableWidth({
    defaultWidth: 360,
    minWidth: 280,
    maxWidth: 640,
    resizeFrom: 'left',
    storageKey: `entity-wizard-sidebar:${sidebarStorageKey}`,
  });

  return (
    <div className="h-screen flex flex-col bg-bg">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to={backTo}
            aria-label="Back"
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-surface-hover hover:bg-surface-active text-heading transition shrink-0"
          >
            <BackArrow size={16} />
          </Link>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-heading truncate">
              New {entityLabel}
              <span className="text-sm text-muted font-normal ml-2">
                — Step {currentStep + 1} of {steps.length}: {steps[currentStep]?.label}
              </span>
            </h1>
            {marketplaceBanner}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="secondary" onClick={onSaveDraft} disabled={!canSaveDraft || isSaving} loading={isSaving}>
            <Save size={14} /> Save draft
          </Button>
          {isLastStep && (
            <Button onClick={onActivate} disabled={!canActivate || isSaving} loading={isSaving}>
              <Check size={14} /> Activate
            </Button>
          )}
        </div>
      </header>

      {/* Step indicator */}
      <StepIndicator
        steps={steps}
        currentStep={currentStep}
        furthestReached={furthestReached}
        onJumpTo={onRequestStep}
      />

      {/* Main + sidebar */}
      <div className="flex-1 flex min-h-0">
        <main className="flex-1 overflow-y-auto p-6 min-w-0">
          {/* Single constrained column, left-aligned — form + wizard-nav
              buttons scroll together. Buttons sit immediately under the last
              form field, matching the form's width. */}
          <div className="max-w-4xl space-y-4">
            {formSlot}
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={onPrevious} disabled={currentStep === 0}>
                <ArrowLeft size={14} /> Previous
              </Button>
              {!isLastStep && (
                <Button onClick={onNext} disabled={!canAdvance || isSaving}>
                  Next <ArrowRight size={14} />
                </Button>
              )}
            </div>
          </div>
        </main>
        <aside className="flex shrink-0 border-l bg-surface" style={{ width: sidebarWidth }}>
          <div
            onMouseDown={onResizeStart}
            className="w-2 cursor-col-resize flex items-center justify-center hover:bg-accent/20 transition-colors group shrink-0"
            title="Drag to resize"
          >
            <GripVertical size={12} className="text-faint group-hover:text-muted" />
          </div>
          <div className="flex-1 flex flex-col min-w-0">
            {assistantSlot ? (
              <Tabs
                aria-label="Sidebar"
                tabs={SIDEBAR_TABS}
                activeTab={tab}
                onChange={(id) => setTab(id === 'help' ? 'help' : 'assistant')}
                className="shrink-0"
              />
            ) : (
              <div className="flex items-center gap-1.5 border-b px-3 py-2.5 shrink-0">
                <BookOpen size={13} className="text-accent-text" />
                <span className="text-xs font-semibold text-heading">Help</span>
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {assistantSlot && tab === 'assistant' ? assistantSlot : helpSlot}
            </div>
          </div>
        </aside>
      </div>

      {navigateAwayWarning}
    </div>
  );
}
